import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getStaffProfile, editProfile, updateProfileImage } from "../api/staff.api";
import { getAssetUrl } from "../api/config";
import { getStaffId, getContactNumber } from "../utils/storage";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import ProfileFamilySection from "../Components/ProfileFamilySection";
import ProfileAddressesSection from "../Components/ProfileAddressesSection";
import Modal from "../Popups/Modal";
import LoadingState from "../Components/States/LoadingState";
import {
  FaClipboardList,
  FaEdit,
  FaShieldAlt,
  FaKey,
  FaUserMinus,
  FaChevronRight,
  FaCamera,
  FaQuestionCircle,
  FaCheckCircle,
} from "react-icons/fa";
import {
  parseBp,
  formatBp,
  parseEyeSight,
  formatEyeSight,
  displayBp,
  displayEyeSight,
  validateHealthDetails,
  clampNumericInput,
  clampRemarks,
  REMARKS_MAX,
  BMI_LIMITS,
  EYE_LIMITS,
} from "../utils/profileValidation";
import { VITAL_LIMITS, preventWheelChange } from "../utils/hraValidation";
import FieldInfo from "../Components/FieldInfo";
import { toast } from "../Components/Toast";

const fieldBoxClass =
  "w-full p-2.5 min-h-[44px] border border-surface-container rounded-xl text-sm text-on-primary-fixed-var bg-surface-lowest focus:outline-none focus:ring-2 focus:ring-primary/40";
const fieldErrorClass = "text-xs text-error mt-1";



const staffToEditForm = (s = {}) => {
  const rawHeight = s.height;
  const heightCm =
    rawHeight && typeof rawHeight === "object"
      ? rawHeight.unit === "imperial"
        ? Number(rawHeight.feet || 0) * 30.48 + Number(rawHeight.inches || 0) * 2.54
        : Number(rawHeight.cm ?? 0)
      : Number(rawHeight || 0);

  const rawWeight = s.weight;
  const weightKg =
    rawWeight && typeof rawWeight === "object"
      ? rawWeight.unit === "imperial"
        ? Number(rawWeight.value || 0) * 0.45359237
        : Number(rawWeight.value ?? 0)
      : Number(rawWeight || 0);

  const unitPreference =
    rawHeight?.unit === "imperial" || rawWeight?.unit === "imperial"
      ? "imperial"
      : "metric";
  const totalInches = Math.round(heightCm / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;

  const bp =
    s.BP && typeof s.BP === "object"
      ? { sys: s.BP.systolic ?? "", dia: s.BP.diastolic ?? "" }
      : parseBp(s.BP);
  const eye = parseEyeSight(s.eyeSight);

  return {
    unitPreference,
    heightCm: heightCm ? Number(heightCm.toFixed(1)) : "",
    heightFeet: feet || "",
    heightInches: heightCm ? inches : "",
    weightKg: weightKg ? Number(weightKg.toFixed(1)) : "",
    weightLbs: weightKg ? Number((weightKg / 0.45359237).toFixed(1)) : "",
    bpSys: bp.sys,
    bpDia: bp.dia,
    BMI:
      typeof s.BMI === "object" && s.BMI !== null
        ? s.BMI.value ?? ""
        : s.BMI ?? "",
    eyeLeft: eye.left,
    eyeRight: eye.right,
    eyeCheckupResults: s.eyeCheckupResults || "",
  };
};


const FieldError = ({ errors, name }) =>
  errors[name] ? <p className={fieldErrorClass}>{errors[name]}</p> : null;

const MyProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [isEditingHealth, setIsEditingHealth] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const id = getStaffId();
    if (id) setStaffId(id);
    else {
      setError("Staff ID not found. Please log in again.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (staffId) fetchStaffProfile();
  }, [staffId]);

  const fetchStaffProfile = () => {
    getStaffProfile(staffId)
      .then((response) => {
        if (response.data.staff) {
          const s = response.data.staff;
          setStaffProfile(s);
          setEditForm(staffToEditForm(s));
        } else setError("Profile data not found.");
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch profile. Please try again later.");
        setLoading(false);
      });
  };

  const handleUnitPreferenceChange = (e) => {
    const nextUnit = e.target.value;
    setEditForm((prev) => {
      if (nextUnit === prev.unitPreference) return prev;
      if (nextUnit === "imperial") {
        const totalInches = Math.round(Number(prev.heightCm || 0) / 2.54);
        return {
          ...prev,
          unitPreference: nextUnit,
          heightFeet: prev.heightCm === "" ? "" : Math.floor(totalInches / 12),
          heightInches: prev.heightCm === "" ? "" : totalInches % 12,
          weightLbs: prev.weightKg === "" ? "" : Number((Number(prev.weightKg) / 0.45359237).toFixed(1)),
        };
      }
      const cm = Number(prev.heightFeet || 0) * 30.48 + Number(prev.heightInches || 0) * 2.54;
      return {
        ...prev,
        unitPreference: nextUnit,
        heightCm: prev.heightFeet === "" && prev.heightInches === "" ? "" : Number(cm.toFixed(1)),
        weightKg: prev.weightLbs === "" ? "" : Number((Number(prev.weightLbs) * 0.45359237).toFixed(1)),
      };
    });
    setFieldErrors({});
  };

  const handleHealthChange = (e) => {
    const { name, value } = e.target;
    let next = value;

    const numericRules = {
      heightCm: { min: 50, max: 272, decimals: 1 },
      heightFeet: { min: 1, max: 8, decimals: 0 },
      heightInches: { min: 0, max: 11, decimals: 0 },
      weightKg: { min: 20, max: 300, decimals: 1 },
      weightLbs: { min: 50, max: 650, decimals: 1 },
      bpSys: { min: 70, max: 220, decimals: 0 },
      bpDia: { min: 40, max: 130, decimals: 0 },
      BMI: { min: BMI_LIMITS.min, max: BMI_LIMITS.max, decimals: 1 },
      eyeLeft: { min: EYE_LIMITS.min, max: EYE_LIMITS.max, decimals: 1 },
      eyeRight: { min: EYE_LIMITS.min, max: EYE_LIMITS.max, decimals: 1 },
    };

    if (numericRules[name]) {
      next = clampNumericInput(value, numericRules[name]);
    } else if (name === "eyeCheckupResults") {
      next = clampRemarks(value);
    }

    setEditForm((prev) => ({ ...prev, [name]: next }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined, bp: undefined }));
  };

  const handleSave = async () => {
    const heightCmValue = editForm.unitPreference === "imperial"
      ? Number(editForm.heightFeet || 0) * 30.48 + Number(editForm.heightInches || 0) * 2.54
      : Number(editForm.heightCm);
    const weightKgValue = editForm.unitPreference === "imperial"
      ? Number(editForm.weightLbs) * 0.45359237
      : Number(editForm.weightKg);

    const validationForm = {
      ...editForm,
      height: heightCmValue,
      weight: weightKgValue,
    };
    const result = validateHealthDetails(validationForm);
    const errors = { ...result.errors };

    // Map legacy validator keys to the currently visible unit-specific inputs.
    if (errors.height) {
      errors[editForm.unitPreference === "imperial" ? "heightFeet" : "heightCm"] = errors.height;
      delete errors.height;
    }
    if (errors.weight) {
      errors[editForm.unitPreference === "imperial" ? "weightLbs" : "weightKg"] = errors.weight;
      delete errors.weight;
    }

    if (editForm.unitPreference === "imperial") {
      if (editForm.heightFeet === "" || Number(editForm.heightFeet) < 1 || Number(editForm.heightFeet) > 8) {
        errors.heightFeet = "Height must be between 1 and 8 feet.";
      }
      if (editForm.heightInches === "" || Number(editForm.heightInches) < 0 || Number(editForm.heightInches) > 11) {
        errors.heightInches = "Inches must be between 0 and 11.";
      }
      if (editForm.weightLbs === "" || Number(editForm.weightLbs) < 50 || Number(editForm.weightLbs) > 650) {
        errors.weightLbs = "Weight must be between 50 and 650 lbs.";
      }
    } else {
      if (editForm.heightCm === "" || heightCmValue < 50 || heightCmValue > 272) {
        errors.heightCm = "Height must be between 50 and 272 cm.";
      }
      if (editForm.weightKg === "" || weightKgValue < 20 || weightKgValue > 300) {
        errors.weightKg = "Weight must be between 20 and 300 kg.";
      }
    }

    const systolic = Number(editForm.bpSys);
    const diastolic = Number(editForm.bpDia);
    if (editForm.bpSys === "" || systolic < 70 || systolic > 220) {
      errors.bpSys = "Systolic pressure must be between 70 and 220 mmHg.";
    }
    if (editForm.bpDia === "" || diastolic < 40 || diastolic > 130) {
      errors.bpDia = "Diastolic pressure must be between 40 and 130 mmHg.";
    } else if (editForm.bpSys !== "" && diastolic >= systolic) {
      errors.bpDia = "Diastolic pressure must be strictly less than systolic pressure.";
    }

    setFieldErrors(errors);
    if (!result.valid || Object.keys(errors).some((key) => Boolean(errors[key]))) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setSaving(true);
    setError(null);

    const same = (a, b) => String(a ?? "") === String(b ?? "");

    const currentForm = staffToEditForm(staffProfile);
    const nextBp = formatBp(editForm.bpSys, editForm.bpDia);
    const currentBp = formatBp(currentForm.bpSys, currentForm.bpDia);
    const nextEye = formatEyeSight(editForm.eyeLeft, editForm.eyeRight);

    const payload = {};

    const currentHeightCm = currentForm.heightCm === "" ? "" : Number(currentForm.heightCm);
    const currentWeightKg = currentForm.weightKg === "" ? "" : Number(currentForm.weightKg);

    if (!same(Number(heightCmValue.toFixed(1)), currentHeightCm)) {
      payload.height = editForm.unitPreference === "imperial"
        ? {
          unit: "imperial",
          feet: Number(editForm.heightFeet),
          inches: Number(editForm.heightInches),
        }
        : { unit: "metric", cm: Number(editForm.heightCm) };
    }

    if (!same(Number(weightKgValue.toFixed(1)), currentWeightKg)) {
      payload.weight = editForm.unitPreference === "imperial"
        ? { unit: "imperial", value: Number(editForm.weightLbs) }
        : { unit: "metric", value: Number(editForm.weightKg) };
    }

    if (!same(editForm.BMI, currentForm.BMI)) {
      const bmiValue = Number(editForm.BMI);

      const category =
        bmiValue < 18.5
          ? "Underweight"
          : bmiValue < 25
            ? "Normal"
            : bmiValue < 30
              ? "Overweight"
              : "Obese";

      payload.BMI = {
        value: bmiValue,
        category,
      };
    }

    if (!same(nextBp, currentBp)) {
      const [systolic, diastolic] = nextBp
        .split("/")
        .map(Number);

      payload.BP = {
        systolic,
        diastolic,
      };
    }

    if (!same(nextEye, staffProfile.eyeSight)) {
      payload.eyeSight = nextEye;
    }

    if (
      !same(
        editForm.eyeCheckupResults,
        staffProfile.eyeCheckupResults
      )
    ) {
      payload.eyeCheckupResults = editForm.eyeCheckupResults;
    }

    delete payload.role;
    delete payload.employeeId;
    delete payload.wallet_balance;
    delete payload.forTests;
    delete payload.forDoctors;
    delete payload.forPackages;

    if (!Object.keys(payload).length) {
      setSaving(false);
      setIsEditingHealth(false);
      toast.success("No changes to save.");
      return;
    }

    try {
      await editProfile(staffId, payload);
      setStaffProfile((prev) => ({ ...prev, ...payload }));
      setIsEditingHealth(false);
      setFieldErrors({});
      toast.success("Health details saved successfully.");
      fetchStaffProfile();
    } catch {
      setError("Failed to update profile. Please try again.");
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const cancelHealthEdit = () => {
    setIsEditingHealth(false);
    setFieldErrors({});
    setEditForm(staffToEditForm(staffProfile));
    setError(null);
  };

  const memberSince = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const displayName = (name) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1) : "No Name";

  const getProfileImageUrl = () => {
    if (previewUrl) return previewUrl;
    const src = staffProfile?.profileImage || staffProfile?.image;
    if (!src) return "/logo.png";
    if (src.startsWith("http")) return src;
    return getAssetUrl(src);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should be less than 5MB");
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleUpdatePhoto = async () => {
    if (!selectedFile || !staffId) return;
    setUploadingPhoto(true);
    setError(null);
    const formData = new FormData();
    formData.append("profileImage", selectedFile);
    try {
      const response = await updateProfileImage(staffId, formData);
      if (response.data?.staff) setStaffProfile(response.data.staff);
      setShowPhotoModal(false);
      setSelectedFile(null);
      setPreviewUrl("");
      fetchStaffProfile();
    } catch {
      setError("Failed to update profile image.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  if (loading) {
    return <LoadingState fullPage withHeader message="Loading profile…" />;
  }

  if (error && !staffProfile) {
    return (
      <div className="min-h-screen bg-surface-low flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <p className="text-red-500">{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!staffProfile) return null;

  const contact = getContactNumber(staffProfile) || "—";

  const identityFields = [
    { label: "Full Name", value: displayName(staffProfile.name) },
    { label: "Employee ID", value: staffProfile.employeeId || "—" },
    { label: "Email Address", value: staffProfile.email || "—" },
    { label: "Contact Number", value: contact },
    { label: "Department", value: staffProfile.department || "—" },
    { label: "Age", value: staffProfile.age ?? "—" },
    { label: "Gender", value: staffProfile.gender || "—" },
  ];

  return (
    <div className="min-h-screen bg-surface-low flex flex-col">
      <Header />
      <div className="flex-1 pb-16 lg:pb-0">
        <div className="max-w-2xl lg:max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pt-20">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-on-primary-fixed-var">Profile &amp; Settings</h1>
            <p className="text-on-surface-variant mt-1 text-xs sm:text-sm">
              Manage your corporate identity and application preferences.
            </p>
          </div>

          <div className="space-y-6">
            <section className="bg-surface-lowest border border-surface-container rounded-xl overflow-hidden">
              <div className="px-4 py-6 sm:px-8 sm:py-8">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                  <div className="flex flex-col items-center lg:items-start text-center lg:text-left shrink-0">
                    <div className="relative">
                      <img
                        src={getProfileImageUrl()}
                        alt="Profile"
                        className="w-24 h-24 rounded-full border-4 border-white object-cover shadow-md"
                        onError={(e) => { e.target.src = "/logo.png"; }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPhotoModal(true)}
                        className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full hover:bg-primary-container transition-colors"
                        aria-label="Edit profile photo"
                      >
                        <FaCamera size={12} />
                      </button>
                    </div>
                    <h2 className="text-xl font-bold text-on-primary-fixed-var mt-3 break-words">{displayName(staffProfile.name)}</h2>
                    <p className="text-sm text-on-surface-variant">{staffProfile.role || staffProfile.department || "Employee"}</p>
                    <p className="text-xs text-on-surface-variant mt-1">Member since — {memberSince(staffProfile.createdAt)}</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                      <FaCheckCircle size={12} />
                      Account status — Active
                    </span>
                  </div>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 min-w-0">
                    {identityFields.map(({ label, value }) => (
                      <div key={label} className="min-w-0">
                        <p className="text-xs text-on-surface-variant">{label}</p>
                        <p className="text-sm font-medium text-on-primary-fixed-var mt-0.5 break-words">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-surface-lowest border border-surface-container rounded-xl p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                  <FaClipboardList className="text-primary" />
                  <h2 className="font-semibold text-on-primary-fixed-var">Health Details</h2>
                </div>
                <div className="flex items-center gap-2">
                  {!isEditingHealth ? (
                    <button
                      type="button"
                      onClick={() => { setIsEditingHealth(true); setFieldErrors({}); setError(null); }}
                      className="min-h-[44px] px-4 py-2 text-sm font-medium text-primary border border-primary rounded-xl hover:bg-primary-fixed/10"
                    >
                      <FaEdit className="inline mr-1.5" size={14} />
                      Edit
                    </button>
                  ) : (
                    <>
                      <button type="button" onClick={cancelHealthEdit} className="min-h-[44px] px-4 py-2 text-sm border border-surface-container rounded-xl">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="min-h-[44px] px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl disabled:opacity-50"
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {error && isEditingHealth && <p className="text-sm text-red-500 mb-3">{error}</p>}
              {fieldErrors.bp && <p className="text-sm text-red-500 mb-3">{fieldErrors.bp}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {!isEditingHealth && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="text-xs text-on-surface-variant block mb-1">Unit Preference</label>
                    <p className={fieldBoxClass}>
                      {staffProfile.height?.unit === "imperial" || staffProfile.weight?.unit === "imperial"
                        ? "Imperial (ft / in, lbs)"
                        : "Metric (cm, kg)"}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-on-surface-variant block mb-1">Height {editForm.unitPreference === "imperial" ? "(feet / inches)" : "(cm)"}</label>
                  {isEditingHealth ? (
                    editForm.unitPreference === "imperial" ? (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input type="number" name="heightFeet" value={editForm.heightFeet ?? ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={1} max={8} step="1" placeholder="1–8 ft" />
                            <p className="text-xs text-on-surface-variant mt-1">Feet (1–8)</p>
                            <FieldError errors={fieldErrors} name="heightFeet" />
                          </div>
                          <div>
                            <input type="number" name="heightInches" value={editForm.heightInches ?? ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={0} max={11} step="1" placeholder="0–11 in" />
                            <p className="text-xs text-on-surface-variant mt-1">Inches (0–11)</p>
                            <FieldError errors={fieldErrors} name="heightInches" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <input type="number" name="heightCm" value={editForm.heightCm ?? ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={50} max={272} step="0.1" placeholder="50–272 cm" />
                        <FieldError errors={fieldErrors} name="heightCm" />
                      </>
                    )
                  ) : (
                    <p className={fieldBoxClass}>
                      {staffProfile.height?.unit === "imperial"
                        ? `${staffProfile.height.feet ?? "—"} ft ${staffProfile.height.inches ?? 0} in`
                        : staffProfile.height?.cm != null
                          ? `${staffProfile.height.cm} cm`
                          : typeof staffProfile.height === "number" || typeof staffProfile.height === "string"
                            ? staffProfile.height ? `${staffProfile.height} cm` : "—"
                            : "—"}
                    </p>
                  )}
                  {isEditingHealth && (
                    <button
                      type="button"
                      onClick={() =>
                        handleUnitPreferenceChange({
                          target: {
                            value: editForm.unitPreference === "imperial" ? "metric" : "imperial",
                          },
                        })
                      }
                      className="text-xs text-primary mt-2 hover:underline font-medium"
                    >
                      Switch to {editForm.unitPreference === "imperial" ? "Metric (cm)" : "Imperial (ft/in)"}
                    </button>
                  )}
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant block mb-1">Weight {editForm.unitPreference === "imperial" ? "(lbs)" : "(kg)"}</label>
                  {isEditingHealth ? (
                    <>
                      {editForm.unitPreference === "imperial" ? (
                        <input type="number" name="weightLbs" value={editForm.weightLbs ?? ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={50} max={650} step="0.1" placeholder="50–650 lbs" />
                      ) : (
                        <input type="number" name="weightKg" value={editForm.weightKg ?? ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={20} max={300} step="0.1" placeholder="20–300 kg" />
                      )}
                      <FieldError errors={fieldErrors} name={editForm.unitPreference === "imperial" ? "weightLbs" : "weightKg"} />
                      <button
                        type="button"
                        onClick={() =>
                          handleUnitPreferenceChange({
                            target: {
                              value: editForm.unitPreference === "imperial" ? "metric" : "imperial",
                            },
                          })
                        }
                        className="text-xs text-primary mt-2 hover:underline font-medium"
                      >
                        Switch to {editForm.unitPreference === "imperial" ? "Metric (kg)" : "Imperial (lbs)"}
                      </button>
                    </>
                  ) : (
                    <p className={fieldBoxClass}>
                      {staffProfile.weight?.value != null
                        ? `${staffProfile.weight.value} ${staffProfile.weight.unit === "imperial" ? "lbs" : "kg"}`
                        : typeof staffProfile.weight === "number" || typeof staffProfile.weight === "string"
                          ? staffProfile.weight ? `${staffProfile.weight} kg` : "—"
                          : "—"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant block mb-1">BMI <FieldInfo text={`Body Mass Index — weight(kg) ÷ height(m)². Valid range: ${BMI_LIMITS.min}–${BMI_LIMITS.max}.`} /></label>
                  {isEditingHealth ? (
                    <>
                      <input type="number" name="BMI" value={editForm.BMI || ""} onChange={handleHealthChange} onWheel={preventWheelChange} className={fieldBoxClass} min={BMI_LIMITS.min} max={BMI_LIMITS.max} step="0.1" placeholder={`${BMI_LIMITS.min}–${BMI_LIMITS.max}`} />
                      <FieldError errors={fieldErrors} name="BMI" />
                    </>
                  ) : (

                    <p className={fieldBoxClass}>
                      {staffProfile.BMI?.value != null
                        ? `${staffProfile.BMI.value}${staffProfile.BMI.category
                          ? ` (${staffProfile.BMI.category})`
                          : ""
                        }`
                        : typeof staffProfile.BMI === "number" ||
                          typeof staffProfile.BMI === "string"
                          ? staffProfile.BMI || "—"
                          : "—"}
                    </p>

                  )}
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="text-xs text-on-surface-variant block mb-1">Blood Pressure (mmHg) <FieldInfo text={`Systolic (upper): ${VITAL_LIMITS.bpSys.min}–${VITAL_LIMITS.bpSys.max} mmHg. Diastolic (lower): ${VITAL_LIMITS.bpDia.min}–${VITAL_LIMITS.bpDia.max} mmHg.`} /></label>
                  {isEditingHealth ? (
                    <>
                      <div className="flex gap-2">
                        <input type="number" name="bpSys" value={editForm.bpSys || ""} onChange={handleHealthChange} onWheel={preventWheelChange} placeholder="70–220" className={fieldBoxClass} min={70} max={220} />
                        <span className="self-center text-on-surface-variant">/</span>
                        <input type="number" name="bpDia" value={editForm.bpDia || ""} onChange={handleHealthChange} onWheel={preventWheelChange} placeholder="40–130" className={fieldBoxClass} min={40} max={130} />
                      </div>
                      <FieldError errors={fieldErrors} name="bpSys" />
                      <FieldError errors={fieldErrors} name="bpDia" />
                    </>
                  ) : (

                    <p className={fieldBoxClass}>
                      {staffProfile.BP &&
                        typeof staffProfile.BP === "object" &&
                        staffProfile.BP.systolic != null &&
                        staffProfile.BP.diastolic != null
                        ? `${staffProfile.BP.systolic}/${staffProfile.BP.diastolic}`
                        : displayBp(staffProfile.BP)}
                    </p>

                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-on-surface-variant block mb-1">Eye Sight <FieldInfo text={`Visual acuity for each eye. Enter a value between ${EYE_LIMITS.min}–${EYE_LIMITS.max} (e.g. 6 for 6/6 normal vision).`} /></label>
                  {isEditingHealth ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <input type="number" name="eyeLeft" value={editForm.eyeLeft || ""} onChange={handleHealthChange} onWheel={preventWheelChange} placeholder={`Left eye (${EYE_LIMITS.min}–${EYE_LIMITS.max}, e.g. 6)`} className={fieldBoxClass} min={EYE_LIMITS.min} max={EYE_LIMITS.max} step="0.1" />
                          <FieldError errors={fieldErrors} name="eyeLeft" />
                        </div>
                        <div>
                          <input type="number" name="eyeRight" value={editForm.eyeRight || ""} onChange={handleHealthChange} onWheel={preventWheelChange} placeholder={`Right eye (${EYE_LIMITS.min}–${EYE_LIMITS.max}, e.g. 6)`} className={fieldBoxClass} min={EYE_LIMITS.min} max={EYE_LIMITS.max} step="0.1" />
                          <FieldError errors={fieldErrors} name="eyeRight" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className={fieldBoxClass}>{displayEyeSight(staffProfile.eyeSight)}</p>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-on-surface-variant">Remarks/Notes</label>
                  {isEditingHealth && (
                    <span className="text-xs text-on-surface-variant">
                      {(editForm.eyeCheckupResults || "").length}/{REMARKS_MAX}
                    </span>
                  )}
                </div>
                {isEditingHealth ? (
                  <>
                    <textarea
                      name="eyeCheckupResults"
                      value={editForm.eyeCheckupResults || ""}
                      onChange={handleHealthChange}
                      rows={3}
                      maxLength={REMARKS_MAX}
                      placeholder="Add notes about eye checkup or other health remarks…"
                      className={`${fieldBoxClass} min-h-[88px] resize-y`}
                    />
                    <FieldError errors={fieldErrors} name="eyeCheckupResults" />
                  </>
                ) : (
                  <p className={`${fieldBoxClass} min-h-[4rem] break-words`}>
                    {staffProfile.eyeCheckupResults || "—"}
                  </p>
                )}
              </div>
            </section>

            <ProfileFamilySection />
            <ProfileAddressesSection />

            <section className="bg-surface-lowest border border-surface-container rounded-xl p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-4">
                <FaShieldAlt className="text-primary" />
                <h2 className="font-semibold text-on-primary-fixed-var">Security</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/changepassword")}
                  className="flex items-center justify-between p-4 min-h-[56px] border border-surface-container rounded-xl hover:bg-surface-low transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <FaKey className="text-primary" />
                    <span className="font-medium text-on-primary-fixed-var">Change Password</span>
                  </div>
                  <FaChevronRight className="text-on-surface-variant shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/delete-account")}
                  className="flex items-center justify-between p-4 min-h-[56px] border border-surface-container rounded-xl hover:bg-red-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <FaUserMinus className="text-red-500" />
                    <span className="font-medium text-red-600">Request account deletion</span>
                  </div>
                  <FaChevronRight className="text-red-400 shrink-0" />
                </button>
              </div>
            </section>

            <button
              type="button"
              onClick={() => navigate("/help")}
              className="w-full flex items-center justify-between p-4 min-h-[56px] bg-surface-lowest border border-surface-container rounded-xl hover:bg-surface-low transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <FaQuestionCircle className="text-primary" />
                <span className="font-medium text-on-primary-fixed-var">Customer Support</span>
              </div>
              <FaChevronRight className="text-on-surface-variant shrink-0" />
            </button>
          </div>
        </div>
        <Footer />
      </div>

      <Modal open={showPhotoModal} onClose={closePhotoModal} className="max-w-md">
        <div className="p-5">
          <h2 className="text-lg font-semibold text-on-primary-fixed-var mb-4 pr-8">Update Profile Picture</h2>
          <div className="flex justify-center mb-4">
            <img
              src={getProfileImageUrl()}
              alt="Preview"
              className="w-32 h-32 rounded-full object-cover border-2 border-surface-container"
              onError={(e) => { e.target.src = "/logo.png"; }}
            />
          </div>
          <input type="file" accept="image/*" onChange={handleFileSelect} className="w-full text-sm min-h-[44px]" disabled={uploadingPhoto} />
          <p className="text-xs text-on-surface-variant mt-1 mb-4">JPG, PNG, GIF — max 5MB</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closePhotoModal} className="min-h-[44px] px-4 py-2 border border-surface-container rounded-xl text-sm" disabled={uploadingPhoto}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdatePhoto}
              disabled={!selectedFile || uploadingPhoto}
              className="min-h-[44px] px-4 py-2 bg-primary text-white rounded-xl text-sm disabled:opacity-50"
            >
              {uploadingPhoto ? "Uploading…" : "Update"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MyProfile;
